import { useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon2x } from '../../../../components/Icons/ChevronDownIcon';
import { BannerClockIcon } from '../../../../components/Icons/BannerFormIcons';
import { Market } from '../../../../models/MarketsModels';
import useTimeZoneConversionTable from './useTimeZoneConversionTable';

interface TimeZoneConversionTableProps {
  baseTimezone: string;
  startDate: DateTime | null;
  endDate: DateTime | null;
  markets: Market[];
}

function TimeZoneConversionTable({ baseTimezone, startDate, endDate, markets }: TimeZoneConversionTableProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const { headers, rows } = useTimeZoneConversionTable({ baseTimezone, startDate, endDate, markets });

  if (!baseTimezone || markets.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        marginTop: '2rem',
        borderRadius: '0.75rem',
        boxShadow: '0px 0px 10px 1px rgba(0, 0, 0, 0.08)',
        backgroundColor: theme.palette.monochrome.monoChrome1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          padding: '1rem',
          height: '4.688rem',
        }}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <BannerClockIcon />
          <Box>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 700 }}>
              {t('pages.bannerOnboarding.schedule.timezoneConversions.title')}
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: theme.palette.monochrome.monoChrome4 }}>
              {t('pages.bannerOnboarding.schedule.timezoneConversions.description')}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            paddingLeft: '0.5rem',
            '& svg': { transform: isExpanded ? 'rotate(180deg) scale(1.2)' : 'rotate(0deg) scale(1.2)' },
          }}
        >
          <ChevronDownIcon2x stroke={theme.palette.primaryButton.main} />
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ padding: '1rem' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1rem',
            }}
          >
            {headers.map((header) => (
              <Typography key={header.id} sx={{ fontSize: '1.125rem', fontWeight: 700 }}>
                {header.label}
              </Typography>
            ))}
          </Box>

          {rows.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1rem',
                alignItems: 'center',
                padding: '0.625rem 0',
                borderBottom: index === rows.length - 1 ? 'none' : `1px solid ${theme.palette.monochrome.monoChrome3}`,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '1.125rem' }}>{row.marketName}</Typography>
                <Typography sx={{ fontSize: '1rem', color: theme.palette.monochrome.monoChrome4 }}>
                  {row.timezoneInfo}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '1.125rem' }}>{row.startTime}</Typography>
              <Typography sx={{ fontSize: '1.125rem' }}>{row.endTime}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default TimeZoneConversionTable;
